import React, { useEffect, useState } from 'react';
import { Field, SxStyle } from '../../../../../types';
import Preview from './preview';
import { useDocumentData } from 'react-firebase-hooks/firestore';
import { doc } from 'firebase/firestore';
import firebaseApp from '../../../../../firebase-app';
import { getTeamDataPath } from '../../../../../utils/paths';
import Box from '@mui/material/Box';
import chroma from 'chroma-js';
import ColorSection from './color-section';
import svStrings from '../../../../../sv-strings';
import ColorItem from './color-item';
import Grid2 from '@mui/material/Grid2';
import { PaletteColors } from '../../../../../types/app-config';

const white = '#ffffff';
const black = '#000000';

export const darkenColor = (color: string, amount: number): string => {
  return chroma(color).darken(amount).hex();
};

export const fontColor = (color: string): string => {
  return chroma(color).luminance() < 0.4 ? white : black;
};

export type PaletteColorsKeys = keyof PaletteColors;

const idMainKey: PaletteColorsKeys = 'ID_MAIN';
const idForegroundKey: PaletteColorsKeys = 'ID_FOREGROUND';
const idContrastKey: PaletteColorsKeys = 'ID_CONTRAST';
const componentBackgroundKey: PaletteColorsKeys = 'COMPONENT_BACKGROUND';
const componentForeground: PaletteColorsKeys = 'COMPONENT_FOREGROUND';
const componentBorder: PaletteColorsKeys = 'COMPONENT_BORDER';
const componentForegroundId: PaletteColorsKeys = 'COMPONENT_FOREGROUND_ID';
const buttonBackgroundKey: PaletteColorsKeys = 'BUTTON_BACKGROUND';
const buttonForegroundKey: PaletteColorsKeys = 'BUTTON_FOREGROUND';
const topBarBackgroundKey: PaletteColorsKeys = 'TOP_BAR_BACKGROUND';
const topBarForegroundKey: PaletteColorsKeys = 'TOP_BAR_FOREGROUND';
const topBarContrastKey: PaletteColorsKeys = 'TOP_BAR_CONTRAST';
const socialHeartKey: PaletteColorsKeys = 'SOCIAL_HEART';

type PaletteColorText = {
  name: string;
  description: string;
};

const defaultColors = {
  ID_MAIN: '#FFFFFF',
  ID_FOREGROUND: '#000000',
  ID_CONTRAST: '#000000',
  COMPONENT_BACKGROUND: '#FFFFFF',
  COMPONENT_FOREGROUND: '#1CC25C',
  COMPONENT_BORDER: '#FFFFFF',
  COMPONENT_FOREGROUND_ID: '#000000',
  BUTTON_BACKGROUND: '#000000',
  BUTTON_FOREGROUND: '#FFFFFF',
  SOCIAL_HEART: '#E31B23',
  TOP_BAR_BACKGROUND: '#000000', // From ID_MAIN
  TOP_BAR_FOREGROUND: '#FFFFFF', // Contrast from TOP_BAR_BACKGROUND?
  TOP_BAR_CONTRAST: '#FFFFFF', // From ID_CONTRAST
} satisfies PaletteColors;

enum Sections {
  ID = 'ID',
  COMPONENT = 'COMPONENT',
  INTERACTION = 'INTERACTION',
  TOPBAR = 'TOPBAR',
}

type PaletteColorSections = keyof PaletteColors;

const sections: Record<Sections, PaletteColorSections[]> = {
  [Sections.ID]: [idMainKey, idForegroundKey, idContrastKey],
  [Sections.COMPONENT]: [
    componentBackgroundKey,
    componentForeground,
    componentForegroundId,
    componentBorder,
  ],
  [Sections.INTERACTION]: [buttonBackgroundKey, buttonForegroundKey, socialHeartKey],
  [Sections.TOPBAR]: [topBarBackgroundKey, topBarForegroundKey, topBarContrastKey],
};

const titles = {
  [Sections.ID]: svStrings.theme.sectionId,
  [Sections.COMPONENT]: svStrings.theme.sectionCard,
  [Sections.INTERACTION]: svStrings.theme.sectionButton,
  [Sections.TOPBAR]: svStrings.theme.sectionHeader,
};

const texts: Record<PaletteColorSections, PaletteColorText> = {
  ID_MAIN: {
    name: svStrings.theme.colorNames.idMain,
    description: svStrings.theme.colorTexts.idMain,
  },
  ID_FOREGROUND: {
    name: svStrings.theme.colorNames.idForeground,
    description: svStrings.theme.colorTexts.idForeground,
  },
  ID_CONTRAST: {
    name: svStrings.theme.colorNames.idContrast,
    description: svStrings.theme.colorTexts.idContrast,
  },
  COMPONENT_BACKGROUND: {
    name: svStrings.theme.colorNames.componentBackground,
    description: svStrings.theme.colorTexts.componentBackground,
  },
  COMPONENT_FOREGROUND: {
    name: svStrings.theme.colorNames.componentForeground,
    description: svStrings.theme.colorTexts.componentForeground,
  },
  COMPONENT_BORDER: {
    name: svStrings.theme.colorNames.componentBorder,
    description: svStrings.theme.colorTexts.componentBorder,
  },
  COMPONENT_FOREGROUND_ID: {
    name: svStrings.theme.colorNames.componentForegroundId,
    description: svStrings.theme.colorTexts.componentForegroundId,
  },
  BUTTON_BACKGROUND: {
    name: svStrings.theme.colorNames.buttonBackground,
    description: svStrings.theme.colorTexts.buttonBackground,
  },
  BUTTON_FOREGROUND: {
    name: svStrings.theme.colorNames.buttonForeground,
    description: svStrings.theme.colorTexts.buttonForeground,
  },
  TOP_BAR_BACKGROUND: {
    name: svStrings.theme.colorNames.topBarBackground,
    description: svStrings.theme.colorTexts.topBarBackground,
  },
  TOP_BAR_FOREGROUND: {
    name: svStrings.theme.colorNames.topBarForeground,
    description: svStrings.theme.colorTexts.topBarForeground,
  },
  TOP_BAR_CONTRAST: {
    name: svStrings.theme.colorNames.topBarContrast,
    description: svStrings.theme.colorTexts.topBarContrast,
  },
  SOCIAL_HEART: {
    name: svStrings.theme.colorNames.socialHeart,
    description: svStrings.theme.colorTexts.socialHeart,
  },
};

const styles: SxStyle = {
  container: {
    marginLeft: -2,
    marginRight: -2,
  },
  colors: (theme) => ({
    minWidth: '430px',
    flex: 1,
    [theme.breakpoints.down('md')]: {
      minWidth: 'unset',
    },
  }),
  preview: (theme) => ({
    minWidth: '410px',
    flex: 2,
    [theme.breakpoints.down('md')]: {
      minWidth: 'unset',
    },
  }),
};

interface Props {
  category: {
    name: string;
    fields: Field[];
  };
  teamAbbrv: string;
  setChange: (
    key: string,
    value: string | number | boolean | null,
    validation: any,
    isTeamDataValue: boolean
  ) => void;
  setValidation: (key: string, value: string | number | boolean, validation: any) => void;
}

const Theme = ({ category, setChange, setValidation, teamAbbrv }: Props) => {
  const topBarOverrideField = category.fields.find((field) => field.id === 'topBarOverride');

  const [paletteColors, setPaletteColors] = useState<PaletteColors>(defaultColors);
  const [topBarOverride, setTopBarOverride] = useState<boolean>(
    (topBarOverrideField?.value as boolean) || false
  );

  const [teamConfig, teamConfigLoading, teamConfigError] = useDocumentData(
    doc(firebaseApp.db, getTeamDataPath(teamAbbrv))
  );

  useEffect(() => {
    const palette = {} as PaletteColors;
    category.fields.forEach((field) => {
      const defaultColor = defaultColors[field.id as PaletteColorsKeys];
      palette[field.id as PaletteColorsKeys] = (field.value as string) || defaultColor;
    });
    setPaletteColors(palette);
  }, [category.fields]);

  const handleChange = (
    key: string,
    value: string | number | boolean | null,
    validation: any,
    isTeamDataValue: boolean
  ) => {
    let updatedDependentColors = {};

    const field = category.fields.find((field) => field.key === key);
    if (!field) {
      console.error('Field not found:', key);
      return;
    }

    // Update dependent colors
    if (field?.id === idMainKey) {
      const idMainColor = value as string;

      // COMPONENT_BACKGROUND -> 50% brighter than ID_MAIN
      const componentBackgroundField = category.fields.find(
        (field) => field.id === componentBackgroundKey
      );
      const componentBackgroundValue = chroma(idMainColor as string)
        .brighten(0.5)
        .hex();

      if (componentBackgroundField) {
        updatedDependentColors = {
          [componentBackgroundKey]: componentBackgroundValue,
        };
        setChange(
          componentBackgroundField.key,
          componentBackgroundValue,
          validation,
          isTeamDataValue
        );
        componentBackgroundField.value = componentBackgroundValue;
      }
    }

    setPaletteColors({
      ...paletteColors,
      ...updatedDependentColors,
      [field.id as string]: value,
    });

    setChange(key, value, validation, isTeamDataValue);
  };

  const onToggleTopBar = (enable: boolean) => {
    if (!topBarOverrideField) return;

    setChange(
      topBarOverrideField.key,
      enable,
      topBarOverrideField.validation,
      !!topBarOverrideField.isTeamDataValue
    );
    setTopBarOverride(enable);
  };

  if (teamConfigError) {
    console.error('Error fetching team config:', teamConfigError);
  }

  return (
    <Grid2 container spacing={0} sx={styles.container}>
      <Grid2 size="grow" sx={styles.colors}>
        <Box flex={1}>
          {Object.entries(sections).map(([section, colors]) => (
            <ColorSection
              key={section}
              title={titles[section as unknown as Sections]}
              enableToggle={topBarOverride}
              showToggle={section === Sections.TOPBAR}
              onToggle={onToggleTopBar}
            >
              {colors.map((color) => {
                const field = category.fields.find((field) => field.id === color);
                return field ? (
                  <ColorItem
                    key={field.key}
                    field={field}
                    setChange={handleChange}
                    title={texts[color].name}
                    description={texts[color].description}
                    disabled={section === Sections.TOPBAR && !topBarOverride}
                  />
                ) : null;
              })}
            </ColorSection>
          ))}
        </Box>
      </Grid2>
      <Grid2 size="grow" sx={styles.preview}>
        {paletteColors && (
          <Preview
            topBarOverride={topBarOverride}
            teamName={teamConfig?.name || teamAbbrv}
            logoImageUrl={teamConfig?.logoImageUrl || ''}
            colors={paletteColors}
          />
        )}
      </Grid2>
    </Grid2>
  );
};

export default Theme;
